import { useQuery } from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import { getFinancialSummary } from 'utils/OrderWebAPIUtils';
function isObject(value) {
    return typeof value === 'object' && value !== null;
}
function hasEntity(value) {
    return isObject(value) && 'entity' in value;
}
function isApiFinancialSummary(value) {
    if (!isObject(value))
        return false;
    // Consider it a summary if at least one known numeric field is present
    const keys = [
        'total_revenue',
        'revenue_receivables_total',
        'cost_payables_total',
        'vendor_payables_total',
        'total_cost',
        'pending_accessorial_revenue',
        'unactioned_pending_revenue',
        'unactioned_pending_cogs',
        'margin',
        'margin_pct',
        'total_invoiced',
        'total_carrier_payout',
        'total_vendor_payout',
    ];
    return keys.some((k) => k in value);
}
function isApiFinancialSummaryResponse(value) {
    if (!isObject(value))
        return false;
    return 'summary' in value || 'data' in value;
}
function hasFinancialSummary(value) {
    return isObject(value) && 'financialSummary' in value;
}
export function normalize(value, multiplier = 1) {
    return isNil(value) ? undefined : value * multiplier;
}
export function useOrderRtbStats(orderId) {
    return useQuery(['order-rtb-stats', orderId], async () => {
        if (!orderId)
            throw new Error('orderId is required');
        const res = await getFinancialSummary(orderId);
        const entity = hasEntity(res) ? res.entity : undefined;
        // Surface backend-declared failures
        if (isObject(entity) &&
            'success' in entity &&
            (entity === null || entity === void 0 ? void 0 : entity.success) === false) {
            const message = (entity === null || entity === void 0 ? void 0 : entity.error) ||
                'Failed to fetch order financial summary';
            throw new Error(String(message));
        }
        let s = {};
        if (hasFinancialSummary(entity)) {
            const candidate = entity
                .financialSummary;
            if (isApiFinancialSummary(candidate))
                s = candidate;
        }
        else if (isApiFinancialSummaryResponse(entity)) {
            if (isApiFinancialSummary(entity.summary))
                s = entity.summary;
            else if (isApiFinancialSummary(entity.data))
                s = entity.data;
        }
        else if (isApiFinancialSummary(entity)) {
            s = entity;
        }
        // Direct API passthrough fields
        const totalInvoiced = normalize(s.total_invoiced);
        const unactionedPendingRevenue = normalize(s.unactioned_pending_revenue);
        const totalCarrierPayout = normalize(s.total_carrier_payout);
        const totalVendorPayout = normalize(s.total_vendor_payout);
        const unactionedPendingCOGS = normalize(s.unactioned_pending_cogs);
        // Gross Profit = (Total Revenue + Unactioned Pending Revenue)
        //              - (Carrier Cost + Vendor Cost + Unactioned Pending COGS)
        const hasAnyField = !isNil(totalInvoiced) ||
            !isNil(unactionedPendingRevenue) ||
            !isNil(totalCarrierPayout) ||
            !isNil(totalVendorPayout) ||
            !isNil(unactionedPendingCOGS);
        const grossProfit = hasAnyField
            ? (totalInvoiced !== null && totalInvoiced !== void 0 ? totalInvoiced : 0) +
                (unactionedPendingRevenue !== null && unactionedPendingRevenue !== void 0 ? unactionedPendingRevenue : 0) -
                ((totalCarrierPayout !== null && totalCarrierPayout !== void 0 ? totalCarrierPayout : 0) +
                    (totalVendorPayout !== null && totalVendorPayout !== void 0 ? totalVendorPayout : 0) +
                    (unactionedPendingCOGS !== null && unactionedPendingCOGS !== void 0 ? unactionedPendingCOGS : 0))
            : undefined;
        // Gross Margin = Gross Profit / (Total Revenue + Unactioned Pending Revenue)
        const totalRevenue = (totalInvoiced !== null && totalInvoiced !== void 0 ? totalInvoiced : 0) + (unactionedPendingRevenue !== null && unactionedPendingRevenue !== void 0 ? unactionedPendingRevenue : 0);
        const grossMargin = !isNil(grossProfit) && totalRevenue !== 0
            ? (grossProfit / totalRevenue) * 100
            : undefined;
        // Booked Gross Profit = Total Revenue - (Carrier Cost + Vendor Cost)
        const bookedGrossProfit = !isNil(totalInvoiced) ||
            !isNil(totalCarrierPayout) ||
            !isNil(totalVendorPayout)
            ? (totalInvoiced !== null && totalInvoiced !== void 0 ? totalInvoiced : 0) -
                ((totalCarrierPayout !== null && totalCarrierPayout !== void 0 ? totalCarrierPayout : 0) + (totalVendorPayout !== null && totalVendorPayout !== void 0 ? totalVendorPayout : 0))
            : undefined;
        // Pending Gross Profit = Unactioned Pending Revenue - Unactioned Pending COGS
        const pendingGrossProfit = !isNil(unactionedPendingRevenue) || !isNil(unactionedPendingCOGS)
            ? (unactionedPendingRevenue !== null && unactionedPendingRevenue !== void 0 ? unactionedPendingRevenue : 0) - (unactionedPendingCOGS !== null && unactionedPendingCOGS !== void 0 ? unactionedPendingCOGS : 0)
            : undefined;
        return {
            revenue: normalize(s.revenue_receivables_total),
            carrierCosts: normalize(s.cost_payables_total),
            vendorCosts: normalize(s.vendor_payables_total),
            grossProfit,
            grossMargin,
            unactionedPendingRevenue,
            unbilledApprovedRevenue: normalize(s.revenue_receivables_total),
            pendingAccessorialRevenue: normalize(s.pending_accessorial_revenue),
            unactionedPendingCOGS,
            totalCost: normalize(s.total_cost),
            totalInvoiced,
            totalCarrierPayout,
            totalVendorPayout,
            bookedGrossProfit,
            pendingGrossProfit,
        };
    }, {
        enabled: Boolean(orderId),
        staleTime: 30000,
        retry: false,
    });
}
